import { LOCAL_SAVE_KEY, SAVE_SCHEMA_VERSION, startingGold } from "../constants/board.js";
import { getDifficultyModeProfile, normalizeDifficultyMode } from "../constants/difficulty.js";
import { MAPS } from "../constants/maps.js";
import { createAdaptiveDifficultyState } from "./adaptiveDifficulty.js";

export const createRewardState = () => ({
	claims: {},
	waveClaims: {},
	availableBetweenWaveCacheWave: null,
	activeScoreMultiplier: null,
});

const normalizeCoord = (point) => {
	if (Array.isArray(point)) return [Number(point[0]) || 0, Number(point[1]) || 0];
	if (point && typeof point === "object") {
		return [Number(point.col ?? point.x) || 0, Number(point.row ?? point.y) || 0];
	}
	return [0, 0];
};

const normalizeMapDef = (mapDef, mapIdx = 0) => {
	const fallback = MAPS[mapIdx] || MAPS[0];
	if (!mapDef || typeof mapDef !== "object") return fallback;
	return {
		...fallback,
		...mapDef,
		path: Array.isArray(mapDef.path) ? mapDef.path.map(normalizeCoord) : fallback.path,
	};
};

const parseRunOptions = (optionsOrMapIdx = 0, carryScore = 0) => {
	if (typeof optionsOrMapIdx === "object" && optionsOrMapIdx !== null) {
		const mapIdx = optionsOrMapIdx.mapIdx || 0;
		return {
			mapIdx,
			mapDef: normalizeMapDef(optionsOrMapIdx.mapDef, mapIdx),
			carryScore: optionsOrMapIdx.carryScore || 0,
			runMode: optionsOrMapIdx.runMode || "campaign",
			difficultyMode: normalizeDifficultyMode(optionsOrMapIdx.difficultyMode),
			runSeed: optionsOrMapIdx.runSeed,
		};
	}

	return {
		mapIdx: optionsOrMapIdx || 0,
		mapDef: normalizeMapDef(MAPS[optionsOrMapIdx || 0], optionsOrMapIdx || 0),
		carryScore: carryScore || 0,
		runMode: "campaign",
		difficultyMode: "medium",
		runSeed: undefined,
	};
};

export const createGameState = (optionsOrMapIdx = 0, carryScore = 0) => {
	const { mapIdx, mapDef, carryScore: nextScore, runMode, difficultyMode, runSeed } = parseRunOptions(optionsOrMapIdx, carryScore);
	const difficultyProfile = getDifficultyModeProfile(difficultyMode);
	const gold = Math.round(startingGold({ mapIdx, mapDef }) * difficultyProfile.startingGoldMultiplier);

	return {
		version: SAVE_SCHEMA_VERSION,
		runMode,
		difficultyMode,
		runSeed: runSeed || Date.now() + Math.floor(Math.random() * 1000000),
		nextId: 1,
		towers: [],
		enemies: [],
		projectiles: [],
		particles: [],
		beams: [],
		floatingTexts: [],
		notifications: [],
		soundCues: [],
		gold,
		lives: 20,
		wave: 0,
		score: nextScore,
		mapIdx,
		mapDef,
		paused: false,
		speed: 1,
		waveActive: false,
		spawnQueue: [],
		spawnTimer: 0,
		autoRun: false,
		autoRunQueuedAt: null,
		selectedTower: null,
		selectedPlacedId: null,
		hoverCell: null,
		placementPreview: null,
		adRewards: createRewardState(),
		adaptiveDifficulty: createAdaptiveDifficultyState(),
		stats: {
			kills: 0,
			leaks: 0,
			damage: 0,
			wavesCleared: 0,
			towersBuilt: 0,
			campaignMapsCleared: 0,
			survivalWavesCleared: 0,
			goldSpent: 0,
			towersUpgraded: 0,
			towersSold: 0,
			bossesKilled: 0,
		},
		lastSavedAt: null,
	};
};

export const cloneStateForSave = (state) => {
	if (!state) return null;

	return {
		...state,
		selectedTower: null,
		selectedPlacedId: null,
		hoverCell: null,
		placementPreview: null,
		autoRunQueuedAt: state.autoRun ? state.autoRunQueuedAt || null : null,
		notifications: [],
		soundCues: [],
		particles: [],
		beams: [],
		floatingTexts: [],
		lastSavedAt: Date.now(),
	};
};

export const restoreRunState = (runState) => {
	const state = {
		...createGameState({
			mapIdx: runState?.mapIdx || 0,
			mapDef: normalizeMapDef(runState?.mapDef, runState?.mapIdx || 0),
			carryScore: runState?.score || 0,
			runMode: runState?.runMode || "campaign",
			difficultyMode: normalizeDifficultyMode(runState?.difficultyMode),
			runSeed: runState?.runSeed,
		}),
		...runState,
		version: SAVE_SCHEMA_VERSION,
		difficultyMode: normalizeDifficultyMode(runState?.difficultyMode),
		selectedTower: null,
		selectedPlacedId: null,
		hoverCell: null,
		placementPreview: null,
		notifications: [],
		soundCues: [],
		particles: runState?.particles || [],
		beams: runState?.beams || [],
		floatingTexts: runState?.floatingTexts || [],
		adRewards: {
			...createRewardState(),
			...(runState?.adRewards || {}),
		},
		adaptiveDifficulty: {
			...createAdaptiveDifficultyState(),
			...(runState?.adaptiveDifficulty || {}),
		},
		stats: {
			kills: 0,
			leaks: 0,
			damage: 0,
			towersBuilt: 0,
			campaignMapsCleared: 0,
			survivalWavesCleared: 0,
			goldSpent: 0,
			towersUpgraded: 0,
			towersSold: 0,
			bossesKilled: 0,
			...(runState?.stats || {}),
		},
	};

	state.mapDef = normalizeMapDef(state.mapDef, state.mapIdx || 0);

	let maxId = 0;
	for (const group of [state.towers, state.enemies, state.projectiles, state.particles, state.beams, state.floatingTexts]) {
		for (const item of group || []) maxId = Math.max(maxId, item?.id || 0);
	}
	state.nextId = Math.max(state.nextId || 1, maxId + 1);

	return state;
};

export const createSavePayload = ({ profile, progress, runState, settings }) => ({
	version: SAVE_SCHEMA_VERSION,
	savedAt: Date.now(),
	profile,
	progress,
	runState,
	settings,
});

export const createDefaultProfile = (user = null, existing = {}) => ({
	displayName: existing.displayName || user?.email?.split("@")[0] || "Guest Commander",
	email: user?.email || existing.email || null,
	username: existing.username || null,
	usernameLower: existing.usernameLower || null,
	createdAt: existing.createdAt || Date.now(),
	lastSeenAt: Date.now(),
});

export const deriveCampaignProgress = (runState, previous = {}) => ({
	highestMapUnlocked: Math.max(
		previous.highestMapUnlocked || 0,
		Math.min(MAPS.length - 1, Number(runState?.stats?.campaignMapsCleared) || 0),
	),
	mapsCleared: Math.max(previous.mapsCleared || 0, Number(runState?.stats?.campaignMapsCleared) || 0),
	bestScore: Math.max(previous.bestScore || 0, runState?.score || 0),
	bestSurvivalScore: runState?.runMode === "survival" ? Math.max(previous.bestSurvivalScore || 0, runState?.score || 0) : previous.bestSurvivalScore || 0,
	bestSurvivalWave: runState?.runMode === "survival" ? Math.max(previous.bestSurvivalWave || 0, runState?.wave || 0) : previous.bestSurvivalWave || 0,
	lastMap: runState?.mapIdx || 0,
	lastWave: runState?.wave || 0,
	lastMode: runState?.runMode || previous.lastMode || "campaign",
});

export const getStorageLabel = (user) => (user ? "Cloud save" : `Local save (${LOCAL_SAVE_KEY})`);
